import React, { useState, useEffect } from 'react';
import {
  Award,
  BookOpen,
  Briefcase,
  Check,
  Clock,
  Megaphone,
  Palette,
  Star,
  Users,
  RotateCw,
  Layers,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/common/Layout';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { SEOHead } from '../components/seo/SEOHead';
import { redirectToCheckout } from '../services/stripe/subscriptions';
import { SUBSCRIPTION_TIERS } from '../services/stripe/config';
import { ROUTES, SUBSCRIPTION_PRICES, getPricing } from '../utils/constants';
import { formatPrice } from '../utils/currency';
import { courseService } from '../services/firebase/courses';
import { Course } from '../types/course';

interface CategoryData {
  title: string;
  description: string;
  bullets: string[];
  examples: string[];
  icon: React.ElementType;
  accentText: string;
  accentBg: string;
  accentBorder: string;
  gradient: string;
  tag: string;
  featuredCourse: {
    title: string;
    description: string;
    category: string;
    level: string;
    duration: string;
    modulesCount: number;
    learnerCount: number;
    imageGradient: string;
    thumbnail?: string;
  };
}

/**
 * 12-Month Package Registration Page Component
 */
export const TwelveMonthPackageRegisterPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [realCourses, setRealCourses] = useState<Record<string, Course>>({});

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const { courses } = await courseService.getCourses({ status: 'published' }, { pageSize: 100 });
        
        const mapping: Record<string, Course> = {};
        
        // Helper to find match
        const findMatch = (keywords: string[]) => 
          courses.find(c => keywords.some(k => c.title.toLowerCase().includes(k.toLowerCase()) || c.category.toLowerCase().includes(k.toLowerCase())));

        // 1. AI Foundations
        const foundations = findMatch(['Deep Research', 'ChatGPT', 'Foundation', 'Basics']);
        if (foundations) mapping['AI Foundations & Everyday Use'] = foundations;

        // 2. Visual
        const visual = findMatch(['Art', 'Stable Diffusion', 'Design', 'Video', 'Midjourney']);
        if (visual) mapping['Visual, Design & Media Creation'] = visual;

        // 3. Business
        const business = findMatch(['Business', 'Validating', 'Freelance', 'Growth']);
        if (business) mapping['Business, Freelancing & Growth'] = business;

        // 4. Marketing
        const marketing = findMatch(['Marketing', 'Copy', 'Content', 'Convert']);
        if (marketing) mapping['Content, Marketing & Copywriting'] = marketing;

        setRealCourses(mapping);
      } catch (err) {
        console.error('Failed to fetch courses for landing page', err);
      }
    };
    fetchCourses();
  }, []);

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins > 0 ? `${mins}m` : ''}`;
    }
    return `${mins}m`;
  };

  /**
   * Handle subscription purchase
   */
  const handleStartLearning = async () => {
    try {
      setLoading(true);
      setError(null);

      // Payment-first flow: Redirect directly to Stripe Checkout
      // After payment, user completes registration
      const priceId = process.env.REACT_APP_STRIPE_TWELVE_MONTH_PACKAGE_PRICE_ID;
      
      if (!priceId) {
        throw new Error('12-month package price ID not configured');
      }

      await redirectToCheckout(
        priceId,
        SUBSCRIPTION_TIERS.TWELVE_MONTH_PACKAGE
      );
    } catch (err: any) {
      console.error('Error starting checkout:', err);
      
      // Provide helpful error messages
      if (err.message?.includes('recurring price')) {
        setError('Configuration error: The 12-month package requires a one-time payment price in Stripe. Please contact support.');
      } else if (err.message?.includes('not configured')) {
        setError('The 12-month package is not yet available. Please try again later or contact support.');
      } else {
        setError(err.message || 'Failed to start checkout process. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: Users,
      title: 'Community Access',
      description: 'Connect with peers, share insights, and collaborate on projects',
    },
    {
      icon: Award,
      title: 'Full Platform Access',
      description: '12 months access to 50+ practical AI courses and resources',
    },
    {
      icon: Clock,
      title: 'No Recurring Fees',
      description: 'One-time payment - no subscriptions, no surprises',
    },
  ];

  const courseCategories: CategoryData[] = [
    {
      title: 'AI Foundations & Everyday Use',
      description: 'Build confidence using AI tools in day to day tasks, without technical knowledge.',
      bullets: [
        'Learn how to use AI effectively for work and life',
        'Use AI to communicate and share your message in any language',
        'Develop AI confidence you can apply immediately',
      ],
      examples: [
        'DeepL for translation & localisation',
        'Deep Research with ChatGPT',
        'Automating notes with Otter.ai',
      ],
      icon: BookOpen,
      accentText: 'text-primary-700',
      accentBg: 'bg-primary-100',
      accentBorder: 'border-primary-100',
      gradient: 'from-primary-50 via-white to-primary-100/80',
      tag: 'Daily workflows',
      featuredCourse: {
        title: 'Deep Research with ChatGPT',
        description: 'Master the art of digging deep into any topic using advanced prompting techniques.',
        category: 'AI Foundations',
        level: 'Beginner',
        duration: '45m',
        modulesCount: 6,
        learnerCount: 1245,
        imageGradient: 'from-blue-500 to-indigo-600',
      }
    },
    {
      title: 'Visual, Design & Media Creation',
      description: 'Create visuals, videos, and branded content using AI powered tools.',
      bullets: [
        'Generate images and graphics',
        'Create and edit videos with AI',
        'Produce media and audio with AI',
      ],
      examples: [
        'Canva AI for design',
        'CapCut with AI features',
        'Stable Diffusion for images',
      ],
      icon: Palette,
      accentText: 'text-accent-700',
      accentBg: 'bg-accent-100',
      accentBorder: 'border-accent-100',
      gradient: 'from-accent-50 via-white to-accent-100/70',
      tag: 'Create & publish',
      featuredCourse: {
        title: 'AI Art with Stable Diffusion',
        description: 'Create stunning, professional-grade artwork and assets for your projects.',
        category: 'Design & Media',
        level: 'Intermediate',
        duration: '1h 30m',
        modulesCount: 8,
        learnerCount: 892,
        imageGradient: 'from-rose-400 to-orange-500',
      }
    },
    {
      title: 'Business, Freelancing & Growth',
      description: 'Use AI to support business ideas, freelancing, and growth.',
      bullets: [
        'Validate ideas before investing time or money',
        'Improve proposals, pricing, and client communication',
        'Understand how AI supports small businesses',
      ],
      examples: [
        'Validating business ideas with AI',
        'Researching market trends',
        'Build a website with Durable AI',
      ],
      icon: Briefcase,
      accentText: 'text-secondary-800',
      accentBg: 'bg-secondary-100',
      accentBorder: 'border-secondary-100',
      gradient: 'from-secondary-50 via-white to-secondary-100/80',
      tag: 'Grow with AI',
      featuredCourse: {
        title: 'Validating Business Ideas',
        description: 'Save months of work by testing your business concepts with AI tools.',
        category: 'Business Growth',
        level: 'Beginner',
        duration: '1h',
        modulesCount: 5,
        learnerCount: 2103,
        imageGradient: 'from-teal-400 to-emerald-600',
      }
    },
    {
      title: 'Content, Marketing & Copywriting',
      description: 'Create high quality content faster, even if writing or marketing is not your strength.',
      bullets: [
        'Write blogs, emails, ads, and social posts',
        'Improve SEO and visibility',
        'Generate consistent content ideas',
      ],
      examples: [
        'Copy.ai for marketing copy',
        'Writesonic for blogs & SEO',
        'Content planning with AI',
      ],
      icon: Megaphone,
      accentText: 'text-primary-700',
      accentBg: 'bg-primary-100',
      accentBorder: 'border-primary-100',
      gradient: 'from-primary-50 via-white to-primary-100/80',
      tag: 'Marketing ready',
      featuredCourse: {
        title: 'Marketing Copy that Converts',
        description: 'Generate high-converting sales copy and content calendars in minutes.',
        category: 'Marketing',
        level: 'Intermediate',
        duration: '1h 15m',
        modulesCount: 7,
        learnerCount: 1567,
        imageGradient: 'from-blue-600 to-purple-600',
      }
    },
  ];

  const FlipCard = ({ category }: { category: CategoryData }) => {
    const [isFlipped, setIsFlipped] = useState(false);
    const Icon = category.icon;

    return (
      <div 
        className="group h-full [perspective:1000px] cursor-pointer"
        onClick={() => setIsFlipped(!isFlipped)}
      >
        <div className={`relative h-full w-full transition-all duration-700 [transform-style:preserve-3d] ${isFlipped ? '[transform:rotateY(180deg)]' : ''}`}>
          
          {/* Front Face */}
          <Card
            className="h-full relative overflow-hidden border-0 shadow-lg group-hover:shadow-2xl transition-all duration-300 bg-white [backface-visibility:hidden] [transform:translateZ(0)]"
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${category.gradient} opacity-50`} />
            
            <div className="relative z-10 p-8 flex flex-col h-full">
              <div className="flex flex-col sm:flex-row items-start justify-between gap-4 mb-6">
                <div className="flex items-start gap-4">
                  <div
                    className={`w-14 h-14 rounded-xl flex items-center justify-center bg-white shadow-md border ${category.accentBorder} flex-shrink-0`}
                  >
                    <Icon className={`w-7 h-7 ${category.accentText}`} />
                  </div>
                  <div>
                    <h3 className="font-montserrat font-bold text-2xl text-neutral-900 mb-2 leading-tight">{category.title}</h3>
                    <p className="text-neutral-600 text-base">{category.description}</p>
                  </div>
                </div>

                <span
                  className={`hidden sm:inline-flex px-3 py-1 text-xs font-bold rounded-full border ${category.accentBorder} bg-white/60 backdrop-blur text-neutral-700 uppercase tracking-wide whitespace-nowrap flex-shrink-0`}
                >
                  {category.tag}
                </span>
              </div>

              <div className="grid grid-cols-1 gap-3 mb-8 flex-grow">
                {category.bullets.map((item) => (
                  <div
                    key={item}
                    className="flex items-start gap-3 p-3 bg-white/60 rounded-lg border border-neutral-100/50"
                  >
                    <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${category.accentBg} flex-shrink-0`}>
                      <Check className={`w-3 h-3 ${category.accentText}`} />
                    </div>
                    <span className="text-neutral-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>

              <div className="space-y-3 mt-auto pt-6 border-t border-neutral-100">
                <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider">Example courses you'll explore</p>
                <div className="flex flex-wrap gap-2">
                  {category.examples.map((example) => (
                    <span
                      key={example}
                      className="px-3 py-1.5 rounded-full bg-white border border-neutral-200 text-sm font-medium text-neutral-600 shadow-sm"
                    >
                      {example}
                    </span>
                  ))}
                </div>
              </div>

              {/* Tap to flip hint */}
              <div className="absolute top-4 right-4 text-neutral-300 opacity-0 group-hover:opacity-100 transition-opacity">
                 <RotateCw className="w-5 h-5" />
              </div>
            </div>
          </Card>

          {/* Back Face */}
          <div className="absolute inset-0 h-full w-full [transform:rotateY(180deg)_translateZ(0)] [backface-visibility:hidden]">
            <Card className="h-full border border-neutral-200 overflow-hidden relative bg-white shadow-xl flex flex-col">
              {/* Course Thumbnail Area - Matching /courses page aspect ratio */}
              <div className={`aspect-video w-full bg-gradient-to-br ${category.featuredCourse.imageGradient} relative overflow-hidden flex-shrink-0`}>
                 {category.featuredCourse.thumbnail ? (
                    <img 
                      src={category.featuredCourse.thumbnail} 
                      alt={category.featuredCourse.title}
                      className="w-full h-full object-cover"
                    />
                 ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="w-12 h-12 text-white/50" />
                    </div>
                 )}
                 <div className="absolute top-3 left-3">
                    <span className="bg-primary text-white px-2 py-1 rounded-full text-xs font-semibold shadow-sm">
                      Featured Course
                    </span>
                 </div>
              </div>

              {/* Content */}
              <div className="p-6 flex flex-col flex-grow">
                {/* Category & Level */}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-primary uppercase tracking-wider">
                    {category.featuredCourse.category}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${category.featuredCourse.level.toLowerCase() === 'beginner' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {category.featuredCourse.level}
                  </span>
                </div>

                {/* Title */}
                <h3 className="font-montserrat font-bold text-lg text-neutral-900 mb-2 line-clamp-2">
                  {category.featuredCourse.title}
                </h3>

                {/* Description */}
                <p className="text-neutral-600 text-sm mb-4 line-clamp-3 flex-grow">
                  {category.featuredCourse.description}
                </p>

                {/* Stats Row */}
                <div className="flex items-center justify-between text-xs text-neutral-500 mb-6 border-t border-neutral-100 pt-4">
                   <div className="flex items-center" title="Duration">
                      <Clock className="w-3.5 h-3.5 mr-1" />
                      {category.featuredCourse.duration}
                   </div>
                   <div className="flex items-center" title="Modules">
                      <Layers className="w-3.5 h-3.5 mr-1" />
                      {category.featuredCourse.modulesCount} Modules
                   </div>
                   <div className="flex items-center" title="Learners">
                      <Users className="w-3.5 h-3.5 mr-1" />
                      {category.featuredCourse.learnerCount.toLocaleString()}
                   </div>
                </div>

                {/* Button */}
                <div className="mt-auto">
                  <Button variant="primary" className="w-full justify-center text-sm py-2.5 shadow-md">
                    Start Learning
                  </Button>
                  <button 
                    className="w-full text-center text-neutral-400 text-xs mt-3 flex items-center justify-center gap-1 hover:text-neutral-600 transition-colors"
                    onClick={(e) => {
                        e.stopPropagation(); 
                        setIsFlipped(false);
                    }}
                  >
                    <RotateCw className="w-3 h-3" /> Flip back to overview
                  </button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Custom Header for 12 Month Package Page */}
      <header className="bg-white shadow-sm border-b border-neutral-200 sticky top-0 z-50">
        <div className="container-custom">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link
              to={ROUTES.home}
              className="flex items-center space-x-2 md:space-x-3"
            >
              <div className="w-16 h-16 rounded-lg flex items-center justify-center">
                <img
                  src="/tulogo.png"
                  alt="trAInedup.ai logo"
                  className="h-16 w-auto"
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    target.onerror = null;
                    target.src = '/logo192.png';
                  }}
                />
              </div>
              <span className="hidden md:inline font-montserrat font-bold text-xl text-neutral-800">
                trAInedup.ai
              </span>
            </Link>

            {/* Desktop Auth Buttons */}
            <div className="hidden md:flex items-center space-x-3">
              <Link to={ROUTES.login}>
                <Button variant="ghost" size="sm">
                  Sign In
                </Button>
              </Link>
              <Button 
                variant="primary" 
                size="sm"
                onClick={handleStartLearning}
                loading={loading}
              >
                Start Learning
              </Button>
            </div>

            {/* Mobile Auth Buttons */}
            <div className="flex items-center space-x-2 md:hidden">
              <Link to={ROUTES.login}>
                <Button
                  variant="primary"
                  size="sm"
                  className="bg-primary-100 text-primary-700 hover:bg-primary-200 focus:ring-primary-200 border border-primary-200"
                >
                  Sign In
                </Button>
              </Link>
              <Button
                variant="primary"
                size="sm"
                onClick={handleStartLearning}
                loading={loading}
              >
                Start Learning
              </Button>
            </div>
          </div>
        </div>
      </header>

      <Layout
        showHeader={false}
        showFooter={true}
        showUpgradeBanner={false}
        showChatbot={false}
      >
      <SEOHead
        title={`12 Month AI Learning Package - Just ${SUBSCRIPTION_PRICES.twelve_month_package.display} | trAInedup.ai`}
        description={`Get 12 months access to 50+ practical AI courses for just ${SUBSCRIPTION_PRICES.twelve_month_package.display}. One-time payment, no recurring fees. Futureproof your AI skills and become AI ready in 2026.`}
        canonical="https://trainedup.ai/twelve-month-package"
        keywords={['AI courses', '12 month AI package', 'learn AI', 'AI training deal', 'AI education discount']}
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-primary-50 to-white pt-24 md:pt-32 pb-8 md:pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 bg-white rounded-full shadow-sm border border-accent-200 mb-8 animate-fade-in">
              <Star className="w-5 h-5 text-accent mr-2" />
              <span className="text-accent font-bold tracking-wide uppercase text-sm">Limited Time Offer</span>
            </div>
            
            <h1 className="font-montserrat font-extrabold text-5xl md:text-7xl text-neutral-900 mb-8 tracking-tight leading-tight">
              12 Months of AI Learning
              <span className="block text-primary mt-2">Just {SUBSCRIPTION_PRICES.twelve_month_package.display}</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-neutral-600 mb-10 leading-relaxed max-w-3xl mx-auto">
              Future proof your AI skills with a year of accessing 50+ practical AI courses on our easy-to-use learning platform. One simple payment, no recurring fees. Become AI Ready in 2026.
            </p>

            {/* Price Display */}
            <Card className="max-w-md mx-auto mb-10 border-primary-100 shadow-xl overflow-hidden relative group">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-secondary to-accent"></div>
              <div className="p-8 text-center bg-white relative z-10">
                <div className="mb-4">
                  <span className="text-neutral-400 line-through text-2xl font-medium">{formatPrice(getPricing('standard').amount * 12)}</span>
                  <span className="text-neutral-500 ml-2 text-sm uppercase tracking-wide font-semibold">(regular value)</span>
                </div>
                <div className="mb-6 flex items-baseline justify-center">
                  <span className="text-7xl font-extrabold text-neutral-900 tracking-tighter">{SUBSCRIPTION_PRICES.twelve_month_package.display}</span>
                  <span className="text-neutral-500 text-xl ml-2 font-medium">/ one-time</span>
                </div>
                <div className="inline-flex items-center px-4 py-2 bg-secondary-50 rounded-full border border-secondary-100 text-secondary-700">
                  <Check className="w-5 h-5 mr-2 text-secondary" />
                  <span className="font-bold text-sm">Save 68% - Pay Once, Learn for a Year</span>
                </div>
              </div>
            </Card>

            <div className="flex flex-col items-center space-y-4">
              <Button 
                variant="primary" 
                size="lg"
                onClick={handleStartLearning}
                loading={loading}
                className="px-12 py-5 text-xl font-bold shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 w-full md:w-auto min-w-[300px]"
              >
                Start Learning Today
              </Button>
              <p className="text-neutral-500 text-sm font-medium">
                One off payment – Instant access to 50+ courses after payment
              </p>
            </div>

            {error && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg max-w-md mx-auto animate-fade-in">
                <p className="text-red-700 font-medium">{error}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* As Seen In - Social Proof */}
      <section className="pt-4 pb-12 bg-white border-b border-neutral-100">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-montserrat font-semibold text-lg text-neutral-400 uppercase tracking-widest mb-2">
              As seen in
            </h2>
            <p className="text-sm text-neutral-400 mb-8">
              trAIned up has been featured by leading tech and business publications covering practical AI skills and real-world use cases.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-6 items-center opacity-60 grayscale">
              <div className="flex items-center justify-center h-12 md:h-14">
                <img src="/logos/entrepreneur.svg" alt="trAIned up featured in Entrepreneur" className="max-h-full max-w-full object-contain" />
              </div>
              <div className="flex items-center justify-center h-12 md:h-14">
                <img src="/logos/popular-science.svg" alt="trAIned up featured in Popular Science" className="max-h-full max-w-full object-contain" />
              </div>
              <div className="flex items-center justify-center h-12 md:h-14">
                <img src="/logos/mashable.svg" alt="trAIned up featured in Mashable" className="max-h-full max-w-full object-contain" />
              </div>
              <div className="flex items-center justify-center h-12 md:h-14">
                <img src="/logos/neowin.png" alt="trAIned up featured in Neowin" className="max-h-full max-w-full object-contain" />
              </div>
              <div className="flex items-center justify-center h-12 md:h-14">
                <img src="/logos/financial-post.svg" alt="trAIned up featured in Financial Post" className="max-h-full max-w-full object-contain invert" />
              </div>
              <div className="flex items-center justify-center h-12 md:h-14">
                <img src="/logos/msn.svg" alt="trAIned up featured in MSN" className="max-h-full max-w-full object-contain" />
              </div>
            </div>
            <p className="sr-only">
              Featured by Entrepreneur, Popular Science, Mashable, Neowin, Financial Post, and MSN
            </p>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <div className="py-24 bg-white relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-primary-50 rounded-full mix-blend-multiply filter blur-3xl opacity-30 -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary-50 rounded-full mix-blend-multiply filter blur-3xl opacity-30 translate-x-1/2 translate-y-1/2"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <h2 className="font-montserrat font-bold text-4xl md:text-5xl text-neutral-900 mb-6 tracking-tight">
              Everything You Need to <span className="text-primary">Futureproof Your Skills</span>
            </h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto leading-relaxed">
              Get access to 50+ practical AI courses for an entire year, curated to take you from beginner to advanced.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full">
                  <div className="p-8 flex flex-col items-center text-center h-full">
                    <div className="w-16 h-16 bg-primary-50 rounded-2xl flex items-center justify-center mb-6 shadow-sm">
                      <Icon className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="font-montserrat font-bold text-2xl text-neutral-900 mb-4">
                      {feature.title}
                    </h3>
                    <p className="text-neutral-600 leading-relaxed text-lg">
                      {feature.description}
                    </p>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="py-24 bg-neutral-50 border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="font-montserrat font-bold text-4xl md:text-5xl text-neutral-900 mb-6 tracking-tight">
              How It Works
            </h2>
            <p className="text-xl text-neutral-600">
              Get started in three simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-6xl mx-auto">
            <div className="text-center group">
              <div className="relative mb-8 mx-auto w-24 h-24">
                <div className="absolute inset-0 bg-primary-200 rounded-full opacity-0 group-hover:opacity-20 transform scale-0 group-hover:scale-110 transition-all duration-300"></div>
                <div className="w-24 h-24 bg-white border-4 border-primary rounded-full flex items-center justify-center relative z-10 shadow-lg group-hover:shadow-xl transition-all duration-300">
                  <span className="text-4xl font-extrabold text-primary">1</span>
                </div>
              </div>
              <h3 className="font-montserrat font-bold text-2xl text-neutral-900 mb-4">
                Make Yourself Future Ready
              </h3>
              <p className="text-neutral-600 text-lg leading-relaxed px-4">
                Purchase your 12-month access by making a one off payment.
              </p>
            </div>

            <div className="text-center group">
              <div className="relative mb-8 mx-auto w-24 h-24">
                <div className="absolute inset-0 bg-secondary-200 rounded-full opacity-0 group-hover:opacity-20 transform scale-0 group-hover:scale-110 transition-all duration-300"></div>
                <div className="w-24 h-24 bg-white border-4 border-secondary rounded-full flex items-center justify-center relative z-10 shadow-lg group-hover:shadow-xl transition-all duration-300">
                  <span className="text-4xl font-extrabold text-secondary">2</span>
                </div>
              </div>
              <h3 className="font-montserrat font-bold text-2xl text-neutral-900 mb-4">
                Create Your Account
              </h3>
              <p className="text-neutral-600 text-lg leading-relaxed px-4">
                Set up your account by creating a username and password.
              </p>
            </div>

            <div className="text-center group">
              <div className="relative mb-8 mx-auto w-24 h-24">
                <div className="absolute inset-0 bg-accent-200 rounded-full opacity-0 group-hover:opacity-20 transform scale-0 group-hover:scale-110 transition-all duration-300"></div>
                <div className="w-24 h-24 bg-white border-4 border-accent rounded-full flex items-center justify-center relative z-10 shadow-lg group-hover:shadow-xl transition-all duration-300">
                  <span className="text-4xl font-extrabold text-accent">3</span>
                </div>
              </div>
              <h3 className="font-montserrat font-bold text-2xl text-neutral-900 mb-4">
                Start Learning
              </h3>
              <p className="text-neutral-600 text-lg leading-relaxed px-4">
                Immediate access to all 50+ practical AI courses, community and resources.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* What You Get Access To */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-montserrat font-bold text-4xl md:text-5xl text-neutral-900 mb-6 tracking-tight">
              What You Get Access To
            </h2>
            <p className="text-xl text-neutral-600 max-w-4xl mx-auto leading-relaxed">
              Explore a diverse library of AI courses designed to help you learn by doing across skills, careers, and creative projects.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {courseCategories.map((category) => {
              const realCourse = realCourses[category.title];
              const displayCategory: CategoryData = {
                 ...category,
                 featuredCourse: realCourse ? {
                    title: realCourse.title,
                    description: realCourse.shortDescription || realCourse.description,
                    category: realCourse.category,
                    level: realCourse.difficulty,
                    duration: formatDuration(realCourse.estimatedDuration),
                    modulesCount: realCourse.totalModules,
                    learnerCount: realCourse.enrollmentCount,
                    imageGradient: category.featuredCourse.imageGradient,
                    thumbnail: realCourse.thumbnail,
                 } : category.featuredCourse
              };
              
              return <FlipCard key={category.title} category={displayCategory} />;
            })}
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="py-24 bg-neutral-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-montserrat font-bold text-4xl text-neutral-900 mb-12 text-center tracking-tight">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden">
              <div className="p-8">
                <h3 className="font-montserrat font-bold text-xl text-neutral-900 mb-3 flex items-center">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                    <span className="text-primary font-bold text-lg">?</span>
                  </div>
                  What happens after 12 months?
                </h3>
                <p className="text-neutral-600 text-lg pl-11">
                  Your access will expire after 12 months. You'll receive email reminders before expiration, 
                  and you can choose to purchase another package or upgrade to a subscription plan at that time.
                </p>
              </div>
            </Card>

            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden">
              <div className="p-8">
                <h3 className="font-montserrat font-bold text-xl text-neutral-900 mb-3 flex items-center">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                    <span className="text-primary font-bold text-lg">?</span>
                  </div>
                  Can I upgrade to Premium later?
                </h3>
                <p className="text-neutral-600 text-lg pl-11">
                  Yes! You can upgrade to our Premium plan at any time to unlock expert consultations 
                  and additional premium features.
                </p>
              </div>
            </Card>

            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden">
              <div className="p-8">
                <h3 className="font-montserrat font-bold text-xl text-neutral-900 mb-3 flex items-center">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                    <span className="text-primary font-bold text-lg">?</span>
                  </div>
                  Is there a refund policy?
                </h3>
                <p className="text-neutral-600 text-lg pl-11">
                  We offer a 7-day money back guarantee. If you're not satisfied with the platform within the first 7 days, contact our support team for a full refund, hassle free.
                </p>
              </div>
            </Card>

            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden">
              <div className="p-8">
                <h3 className="font-montserrat font-bold text-xl text-neutral-900 mb-3 flex items-center">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                    <span className="text-primary font-bold text-lg">?</span>
                  </div>
                  Will I be charged again automatically?
                </h3>
                <p className="text-neutral-600 text-lg pl-11">
                  No! This is a one-time payment only. You will never be charged automatically. 
                  Your access simply expires after 12 months with no recurring fees.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="py-24 bg-gradient-to-br from-primary-600 to-primary-800 relative overflow-hidden">
        {/* Abstract shapes */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white opacity-5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-montserrat font-extrabold text-4xl md:text-5xl text-white mb-6 tracking-tight">
            Ready to Futureproof Your AI Skills?
          </h2>
          <p className="text-xl md:text-2xl text-white/90 mb-10 font-light">
            Join hundreds of learners who are already advancing their careers with AI knowledge
          </p>
          <Button 
            variant="accent" 
            size="lg"
            onClick={handleStartLearning}
            loading={loading}
            className="px-12 py-5 text-xl font-bold shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 min-w-[300px]"
          >
            Start Learning for {SUBSCRIPTION_PRICES.twelve_month_package.display}
          </Button>
          <p className="text-white/80 text-sm mt-6 font-medium tracking-wide">
            One-time payment • 12 months access • No recurring fees
          </p>
        </div>
      </div>
    </Layout>
    </>
  );
};

export default TwelveMonthPackageRegisterPage;